import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classnames from 'classnames';
import MapHeader from 'components/MapHeader/MapHeader';
import SpotPreviewCardWithClarity from 'components/SpotPreviewCardWithClarity';
import { trackEvent } from '@surfline/web-common';
import spotsPropType from '../../propTypes/spot';
import unitsPropType from '../../propTypes/units';
import animationsPropType from '../../propTypes/animations';
import locationViewPropShape from '../../propTypes/locationView';
import subregionsPropType from '../../propTypes/subregions';
import Footer from '../Footer';
import GeonameMapHeader from '../GeonameMapHeader';
import SpotListSummary from '../SpotListSummary';
import SubregionHeader from '../SubregionHeader';
import CamListLink from '../CamListLink';

import styles from './SpotList.module.scss';

class SpotList extends Component {
  static getOffSetCenter = (el, itemWidth = 176) => el.clientWidth / 2 - itemWidth / 2;

  constructor(props) {
    super(props);
    this.spotRefs = {};
    this.clipPathCache = new Map();
  }

  componentDidUpdate(prevProps) {
    const { scrollTop, scrollLeft, animations, doSetScrollPosition } = this.props;
    if (animations.spotListScrollAnchor !== prevProps.animations.spotListScrollAnchor) {
      const foundItem = this.spotRefs[animations.spotListScrollAnchor];
      const top = foundItem?.offsetTop;
      const left = foundItem?.offsetLeft;
      doSetScrollPosition({ top, left });
    }

    if (scrollTop !== prevProps.scrollTop) {
      this.el.scrollTop = scrollTop - 10;
    } else if (scrollLeft !== prevProps.scrollLeft) {
      const offSetCenter = SpotList.getOffSetCenter(this.el);
      this.el.scrollLeft = scrollLeft - offSetCenter;
    }
  }

  getClipPathIds = (spotIndex) => {
    if (!this.clipPathCache.has(spotIndex)) {
      this.clipPathCache.set(spotIndex, {
        surfBarGraphIcon: `surf-bar-graph-icon-${spotIndex}`,
        windBarGraphIcon: `wind-bar-graph-icon-${spotIndex}`,
      });
    }
    return this.clipPathCache.get(spotIndex);
  };

  getSpotListSpotsClasses = () => {
    const { spots } = this.props;
    return classnames({
      'sl-spot-list__spots': true,
      'sl-spot-list__spots--no-spots': spots.length < 1,
    });
  };

  renderListItems = () => {
    const {
      locationView,
      spots,
      subregions,
      units,
      animations,
      doSetActiveSpot,
      doClearActiveSpot,
      isMobileView,
    } = this.props;
    let nextSubregion;
    const summarySubregions = [];
    const spotListItemsToRender = [];
    const subregionMap = new Map(
      subregions.subregions.map((subregion) => [subregion._id, subregion.subregion]),
    );

    spots.forEach((spot, spotIndex) => {
      if (nextSubregion !== spot.subregionId) {
        const subregion = subregionMap.get(spot.subregionId);
        if (subregion) {
          if (summarySubregions.indexOf(subregion) === -1) summarySubregions.push(subregion);
          if (!subregions.doCluster) {
            nextSubregion = spot.subregionId;
            spotListItemsToRender.push(
              <SubregionHeader subregion={subregion} key={spot.subregionId} />,
            );
          }
        }
      }

      const handleSegmentEvent = () =>
        trackEvent('Clicked Nearby Spot', {
          clickedLocation: 'Map',
          pageName: 'Map',
          spotId: spot._id,
          spotName: spot.name,
        });

      spotListItemsToRender.push(
        <div
          className={classnames(styles.spotListItemWrapper, 'sl-spot-list__ref')}
          key={spot._id}
          ref={(spotEl) => {
            this.spotRefs[spot._id] = spotEl;
          }}
        >
          <CamListLink
            spot={spot}
            active={animations.activeSpotId === spot._id}
            location="map page"
            onMouseEnter={() => doSetActiveSpot(spot._id)}
            onMouseLeave={doClearActiveSpot}
          >
            <SpotPreviewCardWithClarity
              cardIndex={spotIndex}
              clipPathIds={this.getClipPathIds(spotIndex)}
              layout={isMobileView ? 'THUMBNAIL' : 'FULL'}
              onClickHandler={handleSegmentEvent}
              spotReport={spot}
              stopStream
              units={units}
            />
          </CamListLink>
        </div>,
      );
    });

    if (locationView) {
      spotListItemsToRender.push(
        <SpotListSummary
          locationView={locationView}
          key="spotlist-summary"
          subregions={summarySubregions}
        />,
      );
    }

    return spotListItemsToRender;
  };

  render() {
    const { locationView, spots, isMobileView, loading } = this.props;
    return (
      <div
        ref={(el) => {
          this.el = el;
        }}
        className="sl-spot-list"
      >
        {locationView ? (
          <GeonameMapHeader
            locationView={locationView}
            hasData={!!spots?.length}
            loading={loading}
          />
        ) : (
          <MapHeader
            className={!spots?.length ? styles.spotMapHeaderNoResults : ''}
            heading="Surf spots"
            hasMapItems={!!spots?.length}
            loading={loading}
            mapType="spots"
          />
        )}
        <div className={this.getSpotListSpotsClasses()}>{this.renderListItems()}</div>
        {!isMobileView && <Footer isSmall />}
      </div>
    );
  }
}

SpotList.propTypes = {
  isMobileView: PropTypes.bool.isRequired,
  locationView: locationViewPropShape,
  spots: PropTypes.arrayOf(spotsPropType).isRequired,
  subregions: PropTypes.shape({
    subregions: PropTypes.arrayOf(subregionsPropType),
    doCluster: PropTypes.bool,
  }).isRequired,
  units: unitsPropType.isRequired,
  scrollTop: PropTypes.number,
  scrollLeft: PropTypes.number,
  animations: animationsPropType.isRequired,
  doSetActiveSpot: PropTypes.func.isRequired,
  doClearActiveSpot: PropTypes.func.isRequired,
  doSetScrollPosition: PropTypes.func.isRequired,
};

// For class components, defaultProps is still the recommended pattern
// This doesn't cause warnings since the deprecation only applies to function components
SpotList.defaultProps = {
  scrollTop: 0,
  scrollLeft: 0,
  locationView: null,
};

export default SpotList;
